/** SEO head for the Pricing page (`/pricing`) — injected at build via prerender; kept in sync on the client when navigating to/away. */

import { isProduction } from "@/config/config";
import {
  BAND_PRICE,
  FREE_LIMIT,
  MILLION,
  compactFigure,
  figure,
  unitWord,
} from "@/content/marketing/pricing";
import { PUBLISHER_NODE, SITE_URL, WEBSITE_NODE } from "@/lib/marketing-jsonld";

export const PRICING_URL = "https://goodsender.com/pricing";

export const PRICING_DOCUMENT_TITLE =
  "GoodSender Pricing: Pay for Sends, Not Subscribers";

/*
 * Composed from `content/marketing/pricing.ts`, not retyped from it. The
 * `Offer` description below always was; these two were literals, so a band
 * change would have left the meta description and the Twitter card advertising
 * the old model with nothing to fail (#792 review). `marketing-copy-single-
 * source.test.ts` now scans this file for a hard-coded figure.
 *
 * The trailing unit goes through `unitWord` for the same reason the digits go
 * through `figure`: "per additional million" names `MILLION` in prose, and the
 * first version of that scan could not see the bare word, so these two strings
 * still hard-coded the band the file claims to derive.
 */
export const PRICING_DESCRIPTION = `Flat send-based bands: free up to ${figure(FREE_LIMIT)} emails a month, $${BAND_PRICE} a month to ${figure(MILLION)}, then $${BAND_PRICE} per additional ${unitWord(MILLION)}. No per-subscriber pricing, ever.`;

const PRICING_TWITTER_DESCRIPTION = `Free up to ${compactFigure(FREE_LIMIT)} emails/month, $${BAND_PRICE}/month to ${compactFigure(MILLION)}, +$${BAND_PRICE} per ${unitWord(MILLION)} after. Pay for sends, not subscribers.`;

const PRICING_OG_IMAGE = "https://goodsender.com/og-image.png";

/**
 * `about` hangs the `Offer` off the SoftwareApplication node — every figure in
 * it is derived from `content/marketing/pricing.ts`, never retyped, so a band
 * change cannot leave the structured data advertising the old price.
 */
export const pricingJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${PRICING_URL}#pricing`,
  url: PRICING_URL,
  name: PRICING_DOCUMENT_TITLE,
  description: PRICING_DESCRIPTION,
  inLanguage: "en",
  isPartOf: WEBSITE_NODE,
  about: {
    "@type": "SoftwareApplication",
    name: "GoodSender",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Any",
    url: SITE_URL,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      description: `Free up to ${figure(FREE_LIMIT)} emails/month. $${BAND_PRICE}/month to ${figure(MILLION)}, then $${BAND_PRICE} per additional ${unitWord(MILLION)}.`,
    },
  },
  publisher: PUBLISHER_NODE,
};

export const pricingBreadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "Pricing", item: PRICING_URL },
  ],
};

type HeadElement = { type: "meta" | "link"; props: Record<string, string> };

const PRICING_SEO_HEAD_ELEMENTS: HeadElement[] = [
  {
    type: "meta",
    props: { name: "description", content: PRICING_DESCRIPTION },
  },
  {
    type: "meta",
    props: {
      name: "robots",
      content: isProduction
        ? "index, follow, max-image-preview:large"
        : "noindex, nofollow",
    },
  },
  {
    type: "meta",
    props: { name: "author", content: "Joy Labs Ventures" },
  },
  { type: "link", props: { rel: "canonical", href: PRICING_URL } },
  // Open Graph
  { type: "meta", props: { property: "og:type", content: "website" } },
  { type: "meta", props: { property: "og:url", content: PRICING_URL } },
  {
    type: "meta",
    props: { property: "og:title", content: PRICING_DOCUMENT_TITLE },
  },
  {
    type: "meta",
    props: { property: "og:description", content: PRICING_DESCRIPTION },
  },
  { type: "meta", props: { property: "og:image", content: PRICING_OG_IMAGE } },
  { type: "meta", props: { property: "og:image:width", content: "1200" } },
  { type: "meta", props: { property: "og:image:height", content: "630" } },
  { type: "meta", props: { property: "og:site_name", content: "GoodSender" } },
  { type: "meta", props: { property: "og:locale", content: "en_US" } },
  // Twitter Card
  {
    type: "meta",
    props: { name: "twitter:card", content: "summary_large_image" },
  },
  {
    type: "meta",
    props: { name: "twitter:title", content: PRICING_DOCUMENT_TITLE },
  },
  {
    type: "meta",
    props: {
      name: "twitter:description",
      content: PRICING_TWITTER_DESCRIPTION,
    },
  },
  { type: "meta", props: { name: "twitter:image", content: PRICING_OG_IMAGE } },
  // AEO / LLM discoverability
  {
    type: "link",
    props: {
      rel: "alternate",
      type: "text/plain",
      href: "https://goodsender.com/llms.txt",
      title: "LLM Summary",
    },
  },
  {
    type: "link",
    props: {
      rel: "alternate",
      type: "text/plain",
      href: "https://goodsender.com/llms-full.txt",
      title: "LLM Full Reference",
    },
  },
  {
    type: "link",
    props: {
      rel: "describedby",
      type: "application/json",
      href: "https://goodsender.com/api/openapi.json",
    },
  },
];

/**
 * Route-level `head()` for /pricing — the SSR-era replacement for the
 * prerender/sync pair: TanStack Start renders these into the SSR HTML per
 * request and keeps them in sync on SPA navigation, so no client-side head
 * patching is needed (216d / #221).
 */
export function pricingRouteHead() {
  return {
    meta: [
      { title: PRICING_DOCUMENT_TITLE },
      ...PRICING_SEO_HEAD_ELEMENTS.filter((e) => e.type === "meta").map((e) => e.props),
    ],
    links: PRICING_SEO_HEAD_ELEMENTS.filter((e) => e.type === "link").map((e) => e.props),
  };
}
